/**
 * Argon2id 派生をメインスレッドで実行しないための Worker ラッパ。
 *
 * 設計方針:
 *   - 1 リクエスト = 1 Worker (派生後すぐ terminate して鍵滞在面を最小化)
 *   - 同時実行はミューテックスでシリアライズ (Argon2 64MiB×並列はメモリを枯渇させる)
 *   - エラー (handler レスポンス / onerror / onmessageerror / postMessage 例外)
 *     全経路で必ず terminate と reject を行い、リソースリークも例外伝播漏れも防ぐ
 *   - 結果以外をメインスレッドに残さない (intermediate buffers は worker 側で zeroize 済み)
 *
 * 公開 API:
 *   deriveMasterKeyInWorker(password, salt, params)
 *     argon2.ts の deriveMasterKey と同じ引数・同じ Promise<Uint8Array> を返す。
 */
import { createArgon2Worker } from './argon2-worker-factory'
import type {
  Argon2WorkerParams,
  Argon2WorkerRequest,
  Argon2WorkerResponse,
} from './argon2-worker.types'

// 直前ジョブの完了 (成功/失敗どちらも) を待つだけのチェーン。
// rejection を queue 側に伝播させないことで「前のエラーで後続が止まる」事故を防ぐ。
let queue: Promise<void> = Promise.resolve()
let nextId = 1

export const deriveMasterKeyInWorker = (
  password: string | Uint8Array,
  salt: Uint8Array,
  params: Argon2WorkerParams = {},
): Promise<Uint8Array> => {
  const job = queue.then(() => runOnce(password, salt, params))

  queue = job.then(
    () => undefined,
    () => undefined,
  )

  return job
}

const runOnce = (
  password: string | Uint8Array,
  salt: Uint8Array,
  params: Argon2WorkerParams,
): Promise<Uint8Array> =>
  new Promise<Uint8Array>((resolve, reject) => {
    const worker = createArgon2Worker()
    const id = nextId++
    let settled = false

    const settle = (fn: () => void) => {
      if (settled) return
      settled = true
      worker.onmessage = null
      worker.onerror = null
      worker.onmessageerror = null
      worker.terminate()
      fn()
    }

    worker.onmessage = (ev: MessageEvent<Argon2WorkerResponse>) => {
      const data = ev.data
      if (!data || data.id !== id) return
      if (data.ok) {
        settle(() => resolve(data.result))
      } else {
        settle(() => reject(new Error(data.error)))
      }
    }
    worker.onerror = (ev: ErrorEvent) => {
      settle(() => reject(new Error(ev?.message || 'argon2 worker error')))
    }
    worker.onmessageerror = () => {
      settle(() => reject(new Error('argon2 worker message error')))
    }

    const req: Argon2WorkerRequest = { id, password, salt, params }
    try {
      worker.postMessage(req)
    } catch (e) {
      settle(() =>
        reject(e instanceof Error ? e : new Error('failed to dispatch argon2 worker message')),
      )
    }
  })
